import React, { useContext, useEffect, useState } from "react";
import { UserContext } from "../contexts/userContext";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { BUSINESS_COLLECTION, PROMOTIONS_COLLECTION } from "../utils/constants";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ModalConfirmation from "../components/ModalConfirmation";
import { IPromotion } from "../interfaces";
import CreatePromotionModal from "../components/CreatePromotionModal";
import { convertCurrencyFormat } from "../helpers";
import { getFormattedDate } from "../utils/utils";

const Promotions = () => {
  const { businessData } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [selectedPromotion, setSelectedPromotion] = useState({} as IPromotion);
  const [list, setList] = useState<any>([] as IPromotion[]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [page, setPage] = useState(0);
  const [lastVisible, setLastVisible] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [openModal, setOpenModal] = useState(
    {} as { show: boolean; type: "delete" | "disable" | "" }
  );

  const pageSize = 10;

  const handleClose = () => {
    setShowNewModal(false);
    setOpenModal({ show: false, type: "" });
    setSelectedPromotion({} as IPromotion);
  };

  const getList = async () => {
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      PROMOTIONS_COLLECTION
    );
    setLoading(true);
    let q;
    if (page === 0) {
      q = query(
        subCollectionRef,
        orderBy("createdAt", "desc"),
        limit(pageSize)
      );
    } else {
      q = query(
        subCollectionRef,
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        limit(pageSize)
      );
    }
    const { docs } = await getDocs(q).finally(() => setLoading(false));
    if (docs.length > 0) {
      setList(
        docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }))
      );
      setLastVisible(docs[docs.length - 1] as any);
    } else setList([]);
  };

  const updateItem = async (item: any) => {
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      PROMOTIONS_COLLECTION
    );
    const promotionDoc = doc(subCollectionRef, item.id);
    await updateDoc(promotionDoc, item as any).then(() => {
      getList();
      setSelectedPromotion({} as IPromotion);
      handleClose();
    });
  };

  const handleDelete = async () => {
    setLoading(true);
    const promotionDoc = doc(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      PROMOTIONS_COLLECTION,
      selectedPromotion.id
    );
    await deleteDoc(promotionDoc)
      .then(() => getList())
      .finally(() => handleClose());
  };

  const handleSwitchPromotion = () => {
    const itemToUpdate = {
      ...selectedPromotion,
      isActive: !selectedPromotion.isActive,
    };
    updateItem(itemToUpdate);
    getList();
    handleClose();
  };

  const handleDeleteOrSwitch = () => {
    switch (openModal.type) {
      case "delete":
        return handleDelete();
      case "disable":
        return handleSwitchPromotion();
      default:
        return null;
    }
  };

  const openPromotionModal = () => setShowNewModal(true);

  const handleChangePage = (_: any, newPage: any) => {
    setPage(newPage);
  };

  useEffect(() => {
    if (businessData.uid) getList();
  }, [page, businessData]);

  // useEffect para obtener el conteo total de documentos para la paginación
  useEffect(() => {
    if (businessData.uid) {
      const promotionCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        PROMOTIONS_COLLECTION
      );
      setLoading(true);
      const fetchTotalCount = async () => {
        // Nota: Esta es una consulta costosa, hazla solo una vez si es posible
        const totalDocs = await getDocs(query(promotionCollectionRef));
        setTotalItems(totalDocs.size);
      };
      fetchTotalCount();
    }
  }, [businessData]);
  return (
    <Grid
      container
      sx={{ width: "100%" }}
      display="flex"
      spacing={3}
      p={3}
      mt={10}
    >
      <Grid size={12} display="flex" alignItems="center">
        <Grid size={{ xs: 8, md: 10 }}>
          <Typography fontSize={24}>Promociones</Typography>
        </Grid>
        <Grid size={{ xs: 4, md: 2 }} justifyContent="end">
          <Button
            variant="contained"
            fullWidth
            size="small"
            color="secondary"
            onClick={openPromotionModal}
          >
            Nueva promoción
          </Button>
        </Grid>
      </Grid>
      <Grid size={12} display="flex" justifyContent="center">
        {loading ? (
          <CircularProgress size={120} sx={{ mt: 4 }} />
        ) : (
          <>
            {list.length > 0 ? (
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell>Fecha inicio</TableCell>
                      <TableCell>Fecha final</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map((promotion: IPromotion) => (
                      <TableRow key={promotion.id}>
                        <TableCell>{promotion.name}</TableCell>
                        <TableCell>{promotion.type}</TableCell>
                        <TableCell>
                          {convertCurrencyFormat(
                            Number(promotion.value),
                            "ARS"
                          )}
                        </TableCell>
                        <TableCell>{getFormattedDate(promotion.startDate)}</TableCell>
                        <TableCell>{getFormattedDate(promotion.endDate)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            color={promotion.isActive ? "success" : "warning"}
                            label={
                              promotion.isActive ? "Activa" : "Desactivada"
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton
                              onClick={() => {
                                setSelectedPromotion(promotion);
                                openPromotionModal();
                              }}
                            >
                              <span className="material-symbols-rounded">
                                edit
                              </span>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              color="error"
                              onClick={() => {
                                setSelectedPromotion(promotion);
                                setOpenModal({ show: true, type: "delete" });
                              }}
                            >
                              <span className="material-symbols-rounded">
                                delete
                              </span>
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              promotion.isActive ? "Deshabilitar" : "Habilitar"
                            }
                          >
                            <Switch
                              size="small"
                              color="secondary"
                              checked={promotion.isActive}
                              onChange={() => {
                                setSelectedPromotion(promotion);
                                setOpenModal({ show: true, type: "disable" });
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[pageSize]}
                  rowsPerPage={pageSize}
                  component="div"
                  page={page}
                  count={totalItems}
                  onPageChange={handleChangePage}
                />
              </TableContainer>
            ) : (
              <Alert severity="warning">No hay promociones cargadas.</Alert>
            )}
          </>
        )}
      </Grid>

      {openModal.show && (
        <ModalConfirmation
          page="promotion"
          status={selectedPromotion.isActive ? "enabled" : "disabled"}
          openModal={openModal}
          handleClose={handleClose}
          handleSubmit={handleDeleteOrSwitch}
        />
      )}
      {showNewModal && (
        <CreatePromotionModal
          show={showNewModal}
          handleClose={handleClose}
          selectedPromotion={selectedPromotion}
          refetch={getList}
        />
      )}
    </Grid>
  );
};

export default Promotions;
